<template>
  <q-dialog v-model="showOffline" persistent>
    <q-card class="offline-overlay">
      <q-card-section class="text-center">
        <q-icon name="wifi_off" size="64px" color="warning" />
        <h1 class="text-h5 q-mt-md">You're Offline</h1>
        <p class="text-body1 q-mt-sm">
          No internet connection detected. We'll automatically reconnect when
          you're back online.
        </p>
      </q-card-section>

      <q-card-section class="text-center">
        <div v-if="isChecking" class="q-mb-md">
          <q-spinner color="primary" size="24px" />
          <span class="q-ml-sm">Checking connection...</span>
        </div>
        <q-btn
          label="Retry Now"
          color="primary"
          @click="checkConnection"
          :loading="isChecking"
        />
      </q-card-section>

      <q-card-section class="text-center text-caption">
        Last online: {{ lastOnlineTime }}
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useNetworkStatus } from 'src/composables/useNetworkStatus';

const { isOnline, offlineSince } = useNetworkStatus();
const showOffline = ref(false);
const isChecking = ref(false);
let checkInterval: ReturnType<typeof setInterval> | null = null;
let showDelayTimeout: ReturnType<typeof setTimeout> | null = null;

const lastOnlineTime = computed(() => {
  if (!offlineSince.value) return 'Unknown';
  return new Date(offlineSince.value).toLocaleTimeString();
});

// Show overlay when offline (with delay to prevent flash on page load)
watch(isOnline, (online) => {
  // Clear any pending show timeout
  if (showDelayTimeout) {
    clearTimeout(showDelayTimeout);
    showDelayTimeout = null;
  }

  if (!online) {
    // Delay showing the overlay to avoid false positive flash during page initialization
    // navigator.onLine can briefly report false before stabilizing
    showDelayTimeout = setTimeout(() => {
      // Double-check we're still offline before showing
      if (!isOnline.value) {
        showOffline.value = true;
        startAutoCheck();
      }
    }, 1000);
  } else {
    showOffline.value = false;
    stopAutoCheck();
  }
});

async function checkConnection() {
  isChecking.value = true;
  try {
    // Ping a lightweight endpoint
    await fetch('/api/health', { method: 'HEAD', cache: 'no-store' });
    // If successful, browser should fire 'online' event
  } catch {
    // Still offline
  } finally {
    isChecking.value = false;
  }
}

function startAutoCheck() {
  // Check every 5 seconds
  checkInterval = setInterval(checkConnection, 5000);
}

function stopAutoCheck() {
  if (checkInterval) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
}

onMounted(() => {
  // Don't show overlay immediately on mount - let the watch handle it with delay
  // This prevents false positive flash when navigator.onLine briefly reports false during page load
});

onUnmounted(() => {
  stopAutoCheck();
  if (showDelayTimeout) {
    clearTimeout(showDelayTimeout);
    showDelayTimeout = null;
  }
});
</script>

<style scoped>
.offline-overlay {
  min-width: 320px;
  max-width: 400px;
}
</style>
